import React, { useState } from 'react';
import { HeartPulse, Lock, User, ArrowRight, ShieldCheck, UserPlus } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface LoginProps {
  onLogin: (username: string) => void;
}

export const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    const endpoint = isRegistering ? '/api/register' : '/api/login';

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      if (isRegistering) {
        setSuccess('Registration successful! Please log in.');
        setIsRegistering(false);
        setPassword('');
      } else {
        onLogin(data.username);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 font-sans">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        {/* Logo Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-raspberry-600 rounded-2xl text-white shadow-lg mb-4">
            <HeartPulse size={32} />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Cardio-Onc AI</h1>
          <p className="text-slate-500 mt-2 font-medium">Clinical Decision Support Portal</p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
          <div className="p-8 md:p-10">
            <div className="mb-8">
              <h2 className="text-xl font-bold text-slate-900">
                {isRegistering ? 'Create Account' : 'Secure Access'}
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {isRegistering 
                  ? 'Register to access clinical tools.' 
                  : 'Authorized personnel only. Sessions are logged.'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                  Clinical Identifier
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. DR_SMITH_82"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-3.5 text-slate-900 focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
                  Security Key
                </label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-12 pr-4 py-3.5 text-slate-900 focus:ring-2 focus:ring-raspberry-500 focus:border-raspberry-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              <AnimatePresence mode="wait">
                {error && (
                  <motion.p 
                    key="error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-red-500 text-xs font-medium bg-red-50 p-3 rounded-lg border border-red-100"
                  >
                    {error}
                  </motion.p>
                )}
                {success && (
                  <motion.p 
                    key="success"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="text-emerald-600 text-xs font-medium bg-emerald-50 p-3 rounded-lg border border-emerald-100"
                  >
                    {success}
                  </motion.p>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-raspberry-600 hover:bg-raspberry-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-raspberry-200 transition-all flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed group"
              >
                {isLoading ? (
                  <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {isRegistering ? 'Register Account' : 'Initialize Portal'} 
                    {isRegistering ? <UserPlus size={20} /> : <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />}
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <button 
                onClick={() => {
                  setIsRegistering(!isRegistering);
                  setError('');
                  setSuccess('');
                }}
                className="text-sm font-medium text-raspberry-600 hover:text-raspberry-700 transition-colors"
              >
                {isRegistering ? 'Already have an account? Log in' : 'Need an account? Register here'}
              </button>
            </div>
          </div>

          {/* Footer Info */}
          <div className="bg-slate-50 border-t border-slate-100 p-6 flex items-center justify-center gap-2">
            <ShieldCheck size={16} className="text-emerald-600" />
            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
              HIPAA Compliant Governance Active
            </span>
          </div>
        </div>

        {/* System Links */}
        <div className="mt-8 flex justify-center gap-6">
          <button className="text-xs font-medium text-slate-400 hover:text-raspberry-600 transition-colors">Forgot Credentials?</button>
          <button className="text-xs font-medium text-slate-400 hover:text-raspberry-600 transition-colors">System Status</button>
          <button className="text-xs font-medium text-slate-400 hover:text-raspberry-600 transition-colors">IT Support</button>
        </div>
      </motion.div>
    </div>
  );
};
